import React, { useState, useEffect } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { outfitService, Outfit } from '../services/outfit';
import { getImageUrl } from '../utils/imageUrl';
import { getDoc, doc } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';

interface CommunityOutfit extends Outfit {
  user_style_bio?: string;
  user_rating?: number; // 1-5, user's rating
  average_rating?: number; // Average of all ratings
}

export const Community: React.FC = () => {
  const [outfits, setOutfits] = useState<CommunityOutfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedPreviewUrls, setResolvedPreviewUrls] = useState<{ [outfitId: string]: string }>({});
  const [userRatings, setUserRatings] = useState<{ [outfitId: string]: number }>({});
  const [commentModal, setCommentModal] = useState<{ isOpen: boolean; outfitId: string | null }>({
    isOpen: false,
    outfitId: null,
  });
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    loadCommunityOutfits();
    loadUserRatings();
  }, []);

  const loadUserRatings = () => {
    // Load user ratings from localStorage
    const saved = localStorage.getItem('outfit_ratings');
    if (saved) {
      try {
        setUserRatings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load user ratings:', e);
      }
    }
  };

  const loadCommunityOutfits = async () => {
    try {
      // Fetch public outfits from all users
      const data = await outfitService.getPublic();
      
      // Load user data for each outfit to get public style bio
      const outfitsWithUserData = await Promise.all(
        data.map(async (outfit) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', outfit.user_id));
            const userData = userDoc.data();
            return {
              ...outfit,
              items: outfit.items || [],
              user_style_bio: userData?.public_style_bio || '',
            };
          } catch (error) {
            console.error(`Failed to load user data for outfit ${outfit.id}:`, error);
            return {
              ...outfit,
              items: outfit.items || [],
              user_style_bio: '',
            };
          }
        })
      );
      
      setOutfits(outfitsWithUserData);
      
      // Resolve preview URLs for outfits that have them
      const previewUrlMap: { [outfitId: string]: string } = {};
      await Promise.all(
        outfitsWithUserData.map(async (outfit) => {
          if (outfit.preview_url) {
            try {
              // Extract file path from URL
              let filePath: string | null = null;
              
              if (outfit.preview_url.includes('/o/')) {
                // Emulator or Firebase Storage URL format
                const match = outfit.preview_url.match(/\/o\/([^?]+)/);
                if (match) {
                  filePath = decodeURIComponent(match[1]);
                }
              } else if (outfit.preview_url.includes('storage.googleapis.com')) {
                // Production URL format: https://storage.googleapis.com/bucket/path
                const match = outfit.preview_url.match(/storage\.googleapis\.com\/[^/]+\/(.+)/);
                if (match) {
                  filePath = match[1];
                }
              }
              
              if (filePath) {
                console.log(`Resolving preview URL for outfit ${outfit.id}:`, filePath);
                const storageRef = ref(storage, filePath);
                const downloadURL = await getDownloadURL(storageRef);
                previewUrlMap[outfit.id] = downloadURL;
                console.log(`Resolved preview URL for outfit ${outfit.id}:`, downloadURL);
              } else {
                // Fallback to original URL
                previewUrlMap[outfit.id] = outfit.preview_url;
              }
            } catch (urlError) {
              console.error(`Failed to resolve preview URL for outfit ${outfit.id}:`, urlError);
              // Fallback to original URL
              previewUrlMap[outfit.id] = outfit.preview_url || '';
            }
          }
        })
      );
      
      setResolvedPreviewUrls(previewUrlMap);
    } catch (err) {
      console.error('Failed to load community outfits:', err);
      setOutfits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingClick = async (outfitId: string, rating: number) => {
    try {
      // Save rating to Firestore
      await outfitService.createOrUpdateRating(outfitId, { rating });
      
      // Update local state
      const newRatings = { ...userRatings, [outfitId]: rating };
      setUserRatings(newRatings);
      localStorage.setItem('outfit_ratings', JSON.stringify(newRatings));
      
      // Update local state
      setOutfits((prev) =>
        prev.map((outfit) =>
          outfit.id === outfitId ? { ...outfit, user_rating: rating } : outfit
        )
      );
    } catch (err) {
      console.error('Failed to save rating:', err);
    }
  };

  const handleCommentClick = (outfitId: string) => {
    setCommentModal({ isOpen: true, outfitId });
    setCommentText('');
  };

  const handleCommentSubmit = async () => {
    if (!commentModal.outfitId || !commentText.trim()) return;
    
    try {
      await outfitService.createComment(commentModal.outfitId, {
        comment: commentText.trim(),
      });
      setCommentModal({ isOpen: false, outfitId: null });
      setCommentText('');
    } catch (err) {
      console.error('Failed to submit comment:', err);
      // Still close modal even on error
      setCommentModal({ isOpen: false, outfitId: null });
      setCommentText('');
    }
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Community Feed</h1>
        <p className="text-gray-600 mb-8">
          Discover outfits from the community. Rate and comment on outfits you love.
        </p>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : outfits.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No community outfits available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {outfits.map((outfit) => {
              const userRating = userRatings[outfit.id] || outfit.user_rating || 0;
              
              return (
                <div
                  key={outfit.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                >
                  {outfit.preview_url ? (
                    <div className="relative aspect-[3/4] bg-gray-100">
                      <img
                        src={resolvedPreviewUrls[outfit.id] || getImageUrl(outfit.preview_url)}
                        alt={outfit.name || 'Outfit preview'}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error(`Failed to load preview for outfit ${outfit.id}:`, outfit.preview_url);
                          console.error(`Resolved URL:`, resolvedPreviewUrls[outfit.id]);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log(`Preview loaded successfully for outfit ${outfit.id}`);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center">
                      <p className="text-gray-400 text-sm">No preview available</p>
                    </div>
                  )}
                  
                  <div className="p-4">
                    {/* User's public style bio */}
                    {outfit.user_style_bio && (
                      <p className="text-sm text-gray-700 mb-3">{outfit.user_style_bio}</p>
                    )}
                    
                    {/* Occasion */}
                    {outfit.occasion && (
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {outfit.occasion}
                        </span>
                      </div>
                    )}
                    
                    {/* Rating hearts */}
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => handleRatingClick(outfit.id, rating)}
                          className={`transition-colors ${
                            rating <= userRating
                              ? 'text-red-500'
                              : 'text-gray-300 hover:text-gray-400'
                          }`}
                          aria-label={`Rate ${rating} out of 5`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill={rating <= userRating ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                          </svg>
                        </button>
                      ))}
                      
                      {/* Comment icon */}
                      <button
                        onClick={() => handleCommentClick(outfit.id)}
                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Add comment"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comment Modal */}
      {commentModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Add Comment</h3>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a private comment..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
              rows={4}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCommentModal({ isOpen: false, outfitId: null })}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCommentSubmit}
                disabled={!commentText.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
